// Safety — Hazardous Chemicals formatting, GHS pills, tags, review-due logic

import React from 'react';
import type { GhsSignal, Chemical } from '../../../../types/chemical.types';
import { REVIEW_DUE_MONTHS } from '../../../../types/chemical.types';

export const DETAIL_TABS = ['Details', 'Activity Logs'] as const;
export type DetailTab = (typeof DETAIL_TABS)[number];

export const todayISO = () => new Date().toISOString().slice(0, 10);
export const uid = (p = 'id') => `${p}_${Math.random().toString(36).slice(2, 9)}`;

// Normalize a date value to YYYY-MM-DD. The backend `date` column may come
// back as a full ISO timestamp ("2025-01-15T00:00:00.000Z") — take the date part.
export function dateOnly(d?: string | null): string {
    if (!d) return '';
    return d.slice(0, 10);
}

export function fmtDate(d?: string | null): string {
    const day = dateOnly(d);
    if (!day) return '–';
    const dt = new Date(`${day}T00:00:00`);
    if (isNaN(dt.getTime())) return d || '–';
    return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function fmtMonthYear(d?: string | null): string {
    const day = dateOnly(d);
    if (!day) return '–';
    const dt = new Date(`${day}T00:00:00`);
    if (isNaN(dt.getTime())) return d || '–';
    return dt.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

// A review is "due" when the last review is older than REVIEW_DUE_MONTHS.
export function isReviewDue(lastReview?: string | null): boolean {
    const day = dateOnly(lastReview);
    if (!day) return true;
    const dt = new Date(`${day}T00:00:00`);
    if (isNaN(dt.getTime())) return false;
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - REVIEW_DUE_MONTHS);
    return dt < cutoff;
}

/* ---------- GHS signal pill ---------- */
export const GhsPill: React.FC<{ signal: GhsSignal }> = ({ signal }) => {
    const cls =
        signal === 'Danger'
            ? 'text-destructive border border-destructive/30 bg-destructive/5'
            : signal === 'Warning'
            ? 'text-amber-600 border border-amber-300 bg-amber-50'
            : 'text-muted-foreground border border-border bg-muted/40';
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{signal}</span>;
};

/* ---------- small inline tags used in the name cell ---------- */
export const DgTag: React.FC = () => (
    <span className="text-[10px] font-semibold text-destructive bg-destructive/10 px-1.5 py-0.5 rounded">DG</span>
);
export const NoSdsTag: React.FC = () => (
    <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded">No SDS</span>
);
export const ReviewDueTag: React.FC = () => (
    <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Review Due</span>
);

export function sdsStatusOf(c: Chemical): 'Attached' | 'Missing' {
    return c.sds ? 'Attached' : 'Missing';
}
